import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ContactForm } from "@/components/home/ContactForm";
import { PlatformChangelog } from "@/components/platform-landing/PlatformChangelog";
import { PlatformCompare } from "@/components/platform-landing/PlatformCompare";
import { PlatformEmbed } from "@/components/platform-landing/PlatformEmbed";
import { PlatformFaq } from "@/components/platform-landing/PlatformFaq";
import { PlatformFeatures } from "@/components/platform-landing/PlatformFeatures";
import { PlatformFinalCta } from "@/components/platform-landing/PlatformFinalCta";
import { PlatformHero } from "@/components/platform-landing/PlatformHero";
import { PlatformJourney } from "@/components/platform-landing/PlatformJourney";
import { PlatformSavings } from "@/components/platform-landing/PlatformSavings";
import { PlatformSegments } from "@/components/platform-landing/PlatformSegments";
import { PlatformShots } from "@/components/platform-landing/PlatformShots";
import { PlatformSteps } from "@/components/platform-landing/PlatformSteps";
import { useReveal } from "@/components/platform-landing/useReveal";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";

import "./PlatformLanding.css";

/**
 * Platform marketing landing — served on ``forgmatic.com`` /
 * ``www.forgmatic.com`` (and the legacy ``.cz`` alias during the
 * cutover). The buyer-facing service landing with the DXF dropzone
 * lives at the tenant-shell ``/`` (``demo.forgmatic.com``,
 * ``*.forgmatic.com``, custom CNAMEs) — see ``Landing.tsx``.
 *
 * Visual language: Claude Design „Forgmatic Platforma v6" (KAN-1329),
 * styled page-scoped in ``PlatformLanding.css`` under ``.pl``.
 */
export default function PlatformLanding(): JSX.Element {
  const { t } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const rootRef = useReveal<HTMLDivElement>();

  useSeo({
    title: t("platformLanding.seo.title"),
    description: t("platformLanding.seo.description"),
    path: "",
    lang,
  });

  return (
    <div className="pl" ref={rootRef}>
      <PlatformHero lang={lang} />
      {/* "Jak se změní váš den" is the one block written in the reader's words
          rather than the product's — an enquiry at 16:42, thirty at once, the
          material going up in price. It used to sit past the halfway mark
          behind three sections about how the product works, so a visitor who
          bounced never reached the only part addressed to them (KAN-1114). */}
      <PlatformCompare />
      <PlatformSteps lang={lang} />
      <PlatformEmbed lang={lang} />
      <PlatformSegments lang={lang} />
      <PlatformFeatures />
      <PlatformShots />
      {/* Where the modules stop being a list and become one job. */}
      <PlatformJourney />
      <PlatformSavings lang={lang} />
      <PlatformFaq />
      <PlatformChangelog lang={lang} />
      {/* Public contact form — lands on the platform admin Kanban
          inbox (``org_id IS NULL``) because no orgSlug is passed. */}
      <ContactForm />
      <PlatformFinalCta lang={lang} />
    </div>
  );
}
